import { FC, RefObject, useRef } from 'react';
import { format, parseISO } from 'date-fns';

import { useIsLoggedIn } from '@zoopla/user';
import { Box } from '@zoopla-eng/zds-components/box';
import { ArrowRightMedium } from '@zoopla-eng/zds-components/icons';
import { useOptimizelyFeatureFlag } from '@zoopla/optimizely';

import { EstimateExplainer } from '../EstimateExplainer/EstimateExplainer';
import Timestamp from '../Timestamp/Timestamp';
import ValueChange from '../ValueChange/ValueChange';
import { ValueChange as ValueChangeType, Maybe } from '../../graphql.generated';
import {
    formatPrice,
    formatRentPrice,
    shortenAndFormatPrice,
} from '../utils/utils';
import { isValidEstimate } from '../utils/estimates';

import {
    estimateWrapperStyle,
    styledRevealEstimateStyle,
    styledEstimateLabelStyle,
    styledTagWrapperStyle,
    valueChangeWrapperStyle,
    estimateLabelWrapperStyle,
    estimatePriceStyle,
    subtitleWrapperStyle,
} from './Estimate.styles.css';
import { Badge } from '@zoopla-eng/zds-components/badges';
import { Button, ButtonLinkRight } from '@zoopla-eng/zds-components/button';
import { fontGroups } from '@zoopla-eng/zds-components/themes/font-groups';
import {
    sinceLastSoldStyle,
    styledEstimatedPriceTextStyle,
} from './Estimate.styles.css';
import { Divider } from '@zoopla-eng/zds-components/divider';

type EstimateType = 'sale' | 'rent';

type EstimateLabelProps = {
    label: 'Low' | 'High';
    price: number;
    type: EstimateType;
};

const EstimateLabel: FC<EstimateLabelProps> = ({ label, price, type }) => {
    const isSale = type === 'sale';
    const priceLabel = isSale ? formatPrice(price) : formatRentPrice(price);

    return (
        <p
            className={estimateLabelWrapperStyle}
            aria-label={`${label} estimate ${priceLabel}`}
        >
            <span className={`${styledEstimateLabelStyle} ${fontGroups.spoon}`}>
                {label}
            </span>
            <span className={isSale ? fontGroups.whisk : fontGroups.fork}>
                {isSale
                    ? shortenAndFormatPrice(price)
                    : formatRentPrice(price, true)}
            </span>
        </p>
    );
};

const DEFAULT_ERROR_CONTENT =
    "We're sorry, but we currently don't have enough information about this property to provide an estimate. We're adding new estimates all the time, so keep checking back.";

export type EstimateProps = {
    type: EstimateType;
    confidenceLevel?: ConfidenceLevel;
    lowerPrice: number;
    currentPrice: number;
    upperPrice: number;
    valueChange?: Maybe<ValueChangeType>;
    hideExplainerLink?: boolean;
    hideSubtitle?: boolean;
    errorText?: string;
    errorLink?: {
        href: string;
        text: string;
    };
    openAuthModal?: (
        triggerOrigin: RefObject<HTMLElement> | null,
        eventLabel: string
    ) => void;
    loginWallEnabled?: boolean;
    ingestedAt?: string | null;
};

export type ConfidenceLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'UNKNOWN';

enum ConfidenceLevelLabels {
    LOW = 'Low confidence',
    MEDIUM = 'Medium confidence',
    HIGH = 'High confidence',
    UNKNOWN = '',
}

const Estimate: FC<React.PropsWithChildren<EstimateProps>> = ({
    type,
    confidenceLevel = 'UNKNOWN',
    currentPrice,
    lowerPrice,
    upperPrice,
    valueChange,
    hideExplainerLink,
    hideSubtitle,
    openAuthModal,
    loginWallEnabled = false,
    errorText = DEFAULT_ERROR_CONTENT,
    errorLink,
    ingestedAt,
}) => {
    const isSale = type === 'sale';

    const [displayCountdown] = useOptimizelyFeatureFlag(
        'hom_countdown_timestamp'
    );
    const isLoggedIn = useIsLoggedIn();
    const showEstimateRevealButton =
        !isLoggedIn && loginWallEnabled && openAuthModal;
    const hideEstimateDetails = !isLoggedIn && loginWallEnabled;
    const revealEstimateOrigin = useRef(null);
    const lastSoldDate = valueChange?.saleDate
        ? format(parseISO(valueChange.saleDate), 'MMMM yyyy')
        : '';

    const subtitle =
        !hideSubtitle && (isSale ? 'Estimated price' : 'Rental estimate');

    if (!isValidEstimate({ currentPrice, lowerPrice, upperPrice })) {
        return (
            <div className={estimateWrapperStyle({ hasError: true })}>
                <p className={fontGroups.ladle}>{errorText}</p>
                {errorLink && (
                    <ButtonLinkRight
                        Icon={ArrowRightMedium}
                        category="tertiary"
                        href={errorLink.href}
                    >
                        {errorLink.text}
                    </ButtonLinkRight>
                )}
            </div>
        );
    }

    return (
        <>
            {subtitle && (
                <div className={subtitleWrapperStyle}>
                    <p className={`${estimatePriceStyle} ${fontGroups.fork}`}>
                        {subtitle}
                    </p>
                </div>
            )}
            <div
                className={estimateWrapperStyle({ hasError: false })}
                data-testid={`${type}-estimate`}
            >
                <EstimateLabel label="Low" price={lowerPrice} type={type} />
                <Divider direction={'horizontal'} />
                <p
                    className={`${styledEstimatedPriceTextStyle({
                        obscure: hideEstimateDetails,
                    })} ${isSale ? fontGroups.teapot : fontGroups.plate}`}
                    data-testid={
                        hideEstimateDetails ? 'estimate-blurred' : 'estimate'
                    }
                    aria-label={isSale ? formatPrice(currentPrice) : undefined}
                >
                    {isSale
                        ? shortenAndFormatPrice(currentPrice)
                        : formatRentPrice(currentPrice)}
                </p>
                <Divider direction={'horizontal'} />
                <EstimateLabel label="High" price={upperPrice} type={type} />
            </div>

            {!hideEstimateDetails && confidenceLevel !== 'UNKNOWN' && (
                <div className={styledTagWrapperStyle}>
                    <Badge
                        level={4}
                        text={ConfidenceLevelLabels[confidenceLevel]}
                    />
                </div>
            )}

            {ingestedAt && (
                <Box
                    pb="S400"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    columnGap="S200"
                >
                    <Timestamp
                        ingestedAt={ingestedAt}
                        displayCountdown={displayCountdown}
                        displayIcon={!displayCountdown}
                    />
                </Box>
            )}

            {!hideEstimateDetails && valueChange && (
                <div
                    className={valueChangeWrapperStyle}
                    data-testid="value-change"
                >
                    <ValueChange
                        numericChange={valueChange.numericChange}
                        percentageChange={valueChange.percentageChange}
                    />
                    <p
                        className={`${sinceLastSoldStyle} ${fontGroups.ladle}`}
                    >{`Since last sold - ${lastSoldDate}`}</p>
                </div>
            )}

            {showEstimateRevealButton && (
                <div className={styledRevealEstimateStyle}>
                    <Button
                        ref={revealEstimateOrigin}
                        category="secondary"
                        onClick={(): void => {
                            openAuthModal &&
                                openAuthModal(
                                    revealEstimateOrigin,
                                    'reveal_estimate'
                                );
                        }}
                    >
                        Reveal the estimate
                    </Button>
                </div>
            )}

            {!hideExplainerLink && <EstimateExplainer />}
        </>
    );
};

export default Estimate;
